import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchBlogBySlug } from "../services/api";
import { Footer } from "../components/common/SharedSections";
import { applySeo, resetSeo } from "../utils/seo";

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "";
const SITE_URL = "https://skillyer.com";

const styles = `
  .blogd-page {
    max-width: 820px;
    margin: 0 auto;
    padding: 105px 20px 60px;
    min-height: 70vh;
  }
  .blogd-breadcrumb {
    font-size: 13px;
    color: #9ca3af;
    margin-bottom: 18px;
  }
  .blogd-breadcrumb a { color: #6246ea; text-decoration: none; }
  .blogd-cat {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #6246ea;
    background: #6246ea14;
    padding: 4px 12px;
    border-radius: 20px;
    margin-bottom: 14px;
  }
  .blogd-title {
    font-family: "Syne", sans-serif;
    font-size: clamp(24px, 4vw, 36px);
    font-weight: 800;
    color: #1a1a2e;
    line-height: 1.25;
    margin-bottom: 10px;
  }
  .blogd-date { font-size: 13px; color: #9ca3af; margin-bottom: 24px; }
  .blogd-head-img {
    width: 100%;
    border-radius: 16px;
    margin-bottom: 30px;
    display: block;
  }
  .blogd-content {
    font-size: 16px;
    color: #333;
    line-height: 1.75;
  }
  .blogd-content h2 {
    font-family: "Syne", sans-serif;
    font-size: 23px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 34px 0 12px;
  }
  .blogd-content h3 {
    font-family: "Syne", sans-serif;
    font-size: 19px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 28px 0 10px;
  }
  .blogd-content p { margin: 0 0 16px; }
  .blogd-content img {
    max-width: 100%;
    border-radius: 12px;
    margin: 18px 0;
    display: block;
  }
  .blogd-content ul, .blogd-content ol { margin: 0 0 16px 22px; }
  .blogd-content li { margin-bottom: 6px; }
  .blogd-content a { color: #6246ea; }
  .blogd-content blockquote {
    border-left: 4px solid #6246ea;
    margin: 18px 0;
    padding: 8px 18px;
    background: #f6f5ff;
    border-radius: 0 10px 10px 0;
    color: #4b5563;
  }
  .blogd-faq-title {
    font-family: "Syne", sans-serif;
    font-size: 23px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 40px 0 16px;
  }
  .blogd-faq {
    border: 1px solid #e8eaf2;
    border-radius: 12px;
    margin-bottom: 10px;
    overflow: hidden;
    background: #fff;
  }
  .blogd-faq summary {
    cursor: pointer;
    padding: 14px 18px;
    font-weight: 600;
    font-size: 15px;
    color: #1a1a2e;
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  .blogd-faq summary::after { content: "+"; font-size: 18px; color: #6246ea; }
  .blogd-faq[open] summary::after { content: "–"; }
  .blogd-faq-a {
    padding: 0 18px 16px;
    font-size: 14.5px;
    color: #4b5563;
    line-height: 1.65;
  }
  .blogd-notfound { text-align: center; padding: 100px 20px; }
  .blogd-notfound h1 { font-family: "Syne", sans-serif; color: #1a1a2e; }
  .blogd-back {
    display: inline-block;
    margin-top: 18px;
    color: #6246ea;
    font-weight: 600;
    text-decoration: none;
  }
`;

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    setLoading(true);
    fetchBlogBySlug(slug)
      .then((res) => {
        const b = res.data.data;
        setBlog(b);
        let parsed = [];
        try {
          parsed = JSON.parse(b.faqs || "[]");
        } catch {}
        setFaqs(Array.isArray(parsed) ? parsed : []);

        const canonical = `${SITE_URL}/blog/${b.slug}`;
        const jsonLd = [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: b.title,
            description: b.metaDescription,
            datePublished: b.createdAt,
            dateModified: b.updatedAt,
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            author: { "@type": "Organization", name: "SkillYer", url: SITE_URL },
            publisher: { "@type": "Organization", name: "SkillYer", url: SITE_URL },
          },
        ];
        if (parsed.length > 0) {
          jsonLd.push({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: parsed.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          });
        }
        if (b.customSchema) {
          try {
            jsonLd.push(JSON.parse(b.customSchema));
          } catch {}
        }
        applySeo({
          title: b.seoTitle,
          description: b.metaDescription,
          keywords: b.keywords,
          canonical,
          jsonLd,
        });
      })
      .catch(() => setBlog(null))
      .finally(() => setLoading(false));

    return resetSeo;
  }, [slug]);

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="blogd-page">
          <div className="blogd-notfound">Loading article…</div>
        </div>
      </>
    );
  }

  if (!blog) {
    return (
      <>
        <style>{styles}</style>
        <div className="blogd-page">
          <div className="blogd-notfound">
            <h1>Article not found</h1>
            <p style={{ color: "#6b7280" }}>
              This article may have been moved or unpublished.
            </p>
            <span
              className="blogd-back"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/blog")}
            >
              ← Back to Blog
            </span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="blogd-page">
        <div className="blogd-breadcrumb">
          <Link to="/">Home</Link> › <Link to="/blog">Blog</Link> ›{" "}
          {blog.title}
        </div>
        <span className="blogd-cat">{blog.category}</span>
        <h1 className="blogd-title">{blog.title}</h1>
        <div className="blogd-date">
          Published {fmtDate(blog.createdAt)}
          {blog.updatedAt !== blog.createdAt &&
            ` · Updated ${fmtDate(blog.updatedAt)}`}
        </div>

        {blog.headImage && (
          <img
            className="blogd-head-img"
            src={`${SERVER_URL}${blog.headImage}`}
            alt={blog.headImageAlt || blog.title}
          />
        )}

        <div
          className="blogd-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {faqs.length > 0 && (
          <section>
            <h2 className="blogd-faq-title">Frequently Asked Questions</h2>
            {faqs.map((f, i) => (
              <details className="blogd-faq" key={i}>
                <summary>{f.q}</summary>
                <div className="blogd-faq-a">{f.a}</div>
              </details>
            ))}
          </section>
        )}
      </div>
      <Footer />
    </>
  );
};

export default BlogDetailPage;
